<template>
  <div class="card-outside">
    <mf-icon fill color="cinza-d20" wght="600" size="24" class="cursor-pointer close-icon" icon="close" @click="$emit('close')" />
    <div class="card-container pt-6 px-4 pb-5" :style="`border-color: ${color}`">
      <div class="d-flex align-center title-container mb-2">
        <div :style="`background-color: ${color}`" class="emoji-container d-flex align-center justify-center">
          <span>{{ icon }}</span>
        </div>
        <h1 class="mb-0 ml-2">{{ title }}</h1>
      </div>
      <p class="mb-0 pb-4 subtitle">{{ subtitle }}</p>

      <div class="cards-grid">
        <div v-for="card in cards" :key="card.id" class="card-item">
          <div v-if="loading" class="loading-container">
            <div class="loader1">
              <v-skeleton-loader type="text" />
            </div>
            <div class="loader2">
              <v-skeleton-loader type="text" />
            </div>
          </div>
          <div v-else>
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <mf-icon :icon="card.icon" color="cinza-l20" size="20" class="mr-1" />
                <h2 class="mb-0">{{ card.value }}</h2>
                <mf-quick-actions
                  v-if="card.hasOwnProperty('quickAction')"
                  ref="quickAction"
                  :quick-action-group-id="`rfv-clients-group-${variant}`"
                  :short-description="quickActionDescription"
                  :long-description="quickActionDescription"
                  :segmentation-group="segmentationGroup"
                />
              </div>

              <mf-tooltip v-if="card.info" max-width="270" top variant="secondary">
                <template #activator="{ on }">
                  <mf-icon dense color="cinza-0" icon="info" />
                </template>
                {{ card.info }}
              </mf-tooltip>
            </div>
            <h3 class="mb-0">{{ card.title }}</h3>
            <mf-tooltip max-width="270" bottom variant="secondary" :disabled="card.id !== '2'">
              <template #activator="{ on }">
                <span>{{ card.subtitle }}</span>
              </template>
              <span>Comparação com o faturamento total do clube no período analisado </span>
            </mf-tooltip>
          </div>
        </div>
      </div>

      <div class="d-flex justify-end mt-1">
        <mf-button text color="marca-0-l40" icon="arrow_forward" icon-position="right" @click="$emit('more-details')" />
      </div>

      <h2 class="presence mb-2">Presença nos canais</h2>

      <div class="progress-grid">
        <div v-for="item in progress" :key="item.id" class="progress-item">
          <div v-if="loading" class="loading-container">
            <div class="loader3">
              <v-skeleton-loader type="text" />
            </div>
            <div class="loader2">
              <v-skeleton-loader type="text" />
            </div>
          </div>
          <div v-else>
            <p class="mb-1">{{ item.label }}</p>
            <progress-bar-chart :bar-color="item.color" :text-color="item.color" :percent="item.value" fill />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { localeString } from '@/config/helpers.js'
import ProgressBarChart from '@/components/ProgressBarChart.vue'
import { criteriaGroup, rfvClientsGroupCriteria, isInSegmentCriteria } from '@/components/quick-actions/segmentationGroups.js'
import { DateTime } from 'luxon'
import { rfvAnalysis } from '../../mixins/analise-rfv'
import { rfvGraphDetails } from '../../mixins/rfv-graph-details'

export default {
  name: 'RfvGraphDetails',
  components: { ProgressBarChart },
  mixins: [rfvAnalysis, rfvGraphDetails],
  props: {
    variant: {
      type: String,
      default: 'champions',
      validator: (value) => ['', 'atRisk', 'loyals', 'lowValue', 'champions', 'promising'].includes(value),
    },
    period: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({}),
  computed: {
    segmentationGroup() {
      const criteria = [rfvClientsGroupCriteria(this.analyticsConfiguration._id, this.currentPeriod._id, [this.clientTypesToSegmentCluster[this.variant]])]
      if (this.stateFilters.segmentsObjects?.length) {
        this.stateFilters.segmentsObjects.forEach((segment) => {
          criteria.push(isInSegmentCriteria(segment))
        })
      }
      return criteriaGroup(criteria)
    },
    quickActionDescription() {
      const formatDate = (date) => DateTime.fromFormat(date, 'yyyy-MM-dd').endOf('month').toFormat('MM/yyyy')

      return `${this.title} entre ${formatDate(this.period.initialDate)} e ${formatDate(this.period.finalDate)}`
    },
    cards() {
      return [
        {
          id: '1',
          icon: 'group',
          value: localeString(this.data.clients),
          title: 'Consumidores',
          subtitle: '',
          info: '',
          quickAction: {
            description: this.quickActionDescription,
            segmentationGroup: this.segmentationGroup,
          },
        },
        {
          id: '2',
          icon: 'payments',
          value: 'R$' + localeString(this.data.revenue, 0),
          title: 'Faturamento',
          subtitle: this.data.percentageRevenue + '% do faturamento clube',
          info: '',
        },
        {
          id: '3',
          icon: 'event_repeat',
          value: localeString(this.data.frequency),
          title: 'Frequência',
          subtitle: '',
          info: 'Mediana da quantidade de vezes que um consumidor desse grupo visitou o mercado. Visita não é dia, é cupom.',
        },
        {
          id: '4',
          icon: 'today',
          value: this.formatRecency(this.data.recency),
          title: 'Recência',
          subtitle: '',
          info: `Mediana em dias desde a última visita dos consumidores desse grupo, agrupados em intervalos de 7 dias.`,
        },
      ]
    },
  },
  methods: {
    formatRecency(recency = 0) {
      if (recency === 0) return 'até 7 dias'
      else {
        const start = recency * 7 + 1
        const end = (recency + 1) * 7
        return `${start} a ${end} dias`
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.loading-container {
  .loader1 {
    width: 96px;
  }
  .loader2 {
    width: 147px;
  }
  .loader3 {
    width: 50px;
  }
}

.close-icon {
  position: absolute;
  right: 8px;
  top: 8px;
}
.card-outside {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  position: relative;
}
.card-container {
  width: calc(100% - 20px);
  border: 2px solid transparent;
  border-radius: 8px;
  background: $branco;
  height: 483px;

  .title-container {
    .emoji-container {
      width: 28px;
      height: 28px;
      font-size: 16px;
      border-radius: 8px;
    }

    h1 {
      font-size: 20px;
      font-weight: 700;
      color: $cinza-d10;
      line-height: 120%;
    }
  }

  .subtitle {
    color: $cinza-0;
    font-weight: 400;
    line-height: 150%;
    border-bottom: 1px solid $cinza-l90;
  }
}

.cards-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;

  .card-item {
    flex-basis: calc(50% - 8px);
    padding: 10px 12px;
    background: $cinza-l90;
    border-radius: 8px;
    height: 76px;
    .info-icon {
      margin-left: 2px;
    }
    h2 {
      font-size: 16px;
      color: $cinza-d10;
      font-weight: 600;
      line-height: 20px;
    }

    h3 {
      font-size: 14px;
      color: $cinza-l30;
      font-weight: 400;
      line-height: 1;
    }

    span {
      font-size: 12px;
      color: $cinza-d10;
      font-weight: 400;
      line-height: 120%;
    }
  }
}
.presence {
  color: $cinza-d10;
  font-size: 18px;
  font-weight: 700;
  line-height: 120%;
}

.progress-grid {
  display: flex;
  flex-wrap: wrap;
  row-gap: 8px;
  column-gap: 16px;

  p {
    color: $cinza-0;
    font-size: 12px;
    line-height: 120%;
  }

  .progress-item {
    flex-basis: calc(50% - 8px);
  }
}
</style>
